import { useEffect, useRef, useCallback } from 'react';
import { authenticatedFetch } from '@/lib/api';

const POLL_INTERVAL = 5000; // 5 seconds

/**
 * Global hook that monitors material processing and fires browser
 * notifications when materials finish processing.
 *
 * Mount once at the App level so it works across all pages.
 */
export function useProcessingNotifications() {
    const prevMaterialsRef = useRef(new Map()); // id → status
    const intervalRef = useRef(null);
    const isPollingRef = useRef(false);

    // ── Fire a browser notification ──────────────────────────────
    const notify = useCallback((title, body) => {
        if (Notification.permission !== 'granted') return;

        try {
            const n = new Notification(title, {
                body,
                icon: '/favicon.ico',
                tag: `material-ready-${Date.now()}`,
            });
            n.onclick = () => {
                window.focus();
                n.close();
            };
        } catch (e) {
            console.warn('Notification failed:', e);
        }
    }, []);

    // ── Poll and detect transitions ─────────────────────────────
    const poll = useCallback(async () => {
        try {
            const res = await authenticatedFetch('/api/materials');
            if (!res.ok) return;
            const materials = await res.json();

            const prev = prevMaterialsRef.current;
            const next = new Map();

            for (const m of materials) {
                next.set(m.id, m.status);

                // Detect processing → completed transition
                if (prev.has(m.id) && prev.get(m.id) === 'processing' && m.status === 'completed') {
                    notify('📚 Material Ready!', `"${m.title}" has finished processing and is ready to study.`);
                }
            }

            prevMaterialsRef.current = next;

            // If nothing is processing any more, stop polling
            const hasProcessing = materials.some(m => m.status === 'processing');
            if (!hasProcessing && intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                isPollingRef.current = false;
            }
        } catch (_) { /* network errors are non-fatal */ }
    }, [notify]);

    // ── Start polling (called externally after a successful upload) ──
    const startPolling = useCallback(() => {
        if (isPollingRef.current) return; // already polling
        isPollingRef.current = true;

        // Build the initial snapshot so we don't notify on first fetch
        (async () => {
            try {
                const res = await authenticatedFetch('/api/materials');
                if (res.ok) {
                    const materials = await res.json();
                    const snapshot = new Map();
                    for (const m of materials) {
                        snapshot.set(m.id, m.status);
                    }
                    prevMaterialsRef.current = snapshot;
                }
            } catch (_) { }

            // Then start regular polling
            intervalRef.current = setInterval(poll, POLL_INTERVAL);
        })();
    }, [poll]);

    // ── Request Notification permission ──────────────────────────
    const requestPermission = useCallback(async () => {
        if (!('Notification' in window)) return false;
        if (Notification.permission === 'granted') return true;
        if (Notification.permission === 'denied') return false;

        const result = await Notification.requestPermission();
        return result === 'granted';
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    // Auto-start polling on mount if there are already processing materials
    useEffect(() => {
        (async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const res = await authenticatedFetch('/api/materials');
                if (!res.ok) return;
                const materials = await res.json();

                const snapshot = new Map();
                for (const m of materials) {
                    snapshot.set(m.id, m.status);
                }
                prevMaterialsRef.current = snapshot;

                if (materials.some(m => m.status === 'processing')) {
                    isPollingRef.current = true;
                    intervalRef.current = setInterval(poll, POLL_INTERVAL);
                }
            } catch (_) { }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { startPolling, requestPermission };
}
